(() => {
  'use strict';

  const MAX_TIMEOUT = 2147483647;
  const CART_STATE_EVENT = 'bundle-course:cart-state';

  class BundleCourse extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.itemsExist = false;
      this.cartVerified = false;
      this.cartBusy = false;
      this.lifecycleToken = (this.lifecycleToken || 0) + 1;
      this.cartCheckController = null;
      this.expirationTimeout = null;
      this.alertTimeout = null;
      this.selectionPromptTimeout = null;
      this.shakeTimeouts = new Set();
      // Precio de paquete de 10 clases: solo activo en la sección backup (data-package-10="true")
      this.package10Enabled = this.dataset.package10 === 'true';

      this.countElement = this.querySelector('[data-bundle-count]');
      this.regularPriceElement = this.querySelector('[data-bundle-regular]');
      this.discountedPriceElement = this.querySelector('[data-bundle-discounted]');
      this.savingsMessage = this.querySelector('[data-bundle-savings]');
      this.warningMessage = this.querySelector('[data-bundle-warning]');
      this.selectionPrompt = this.querySelector('[data-bundle-selection-prompt]');
      this.addButton = this.querySelector('[data-bundle-add]');
      this.clearButton = this.querySelector('[data-bundle-clear]');
      this.alertBox = this.querySelector('[data-bundle-alert]');
      this.tabs = [...this.querySelectorAll('[data-bundle-tab]')];
      this.panels = [...this.querySelectorAll('[data-bundle-panel]')];

      this.onClick = this.handleClick.bind(this);
      this.onChange = this.handleChange.bind(this);
      this.onCartState = this.handleCartState.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      document.addEventListener(CART_STATE_EVENT, this.onCartState);

      this.updateArrows();
      this.updateSummary();
      this.checkCart();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      document.removeEventListener(CART_STATE_EVENT, this.onCartState);
      this.cartCheckController?.abort();
      this.cartCheckController = null;
      this.lifecycleToken += 1;
      window.clearTimeout(this.expirationTimeout);
      window.clearTimeout(this.alertTimeout);
      window.clearTimeout(this.selectionPromptTimeout);
      this.selectionPromptTimeout = null;
      this.shakeTimeouts.forEach((timeout) => window.clearTimeout(timeout));
      this.shakeTimeouts.clear();
      this.initialized = false;
    }

    get items() {
      return [...this.querySelectorAll('.bundle-course__item')];
    }

    finishItem(item) {
      const input = item.querySelector('.bundle-course__input');
      const checkbox = item.querySelector('.bundle-course__checkbox');
      const wasFinished = item.dataset.finished === 'true';

      if (input) {
        input.checked = false;
        input.disabled = true;
        input.setAttribute('aria-disabled', 'true');
      }

      item.classList.add('bundle-course__item--finished');
      item.dataset.finished = 'true';
      item.setAttribute('aria-disabled', 'true');
      checkbox?.setAttribute('aria-disabled', 'true');

      if (checkbox && !checkbox.querySelector('.bundle-course__finished-badge')) {
        const badge = document.createElement('span');
        badge.className = 'bundle-course__finished-badge';
        badge.textContent = 'CLASE FINALIZADA';
        checkbox.append(badge);
      }

      return !wasFinished;
    }

    refreshExpiredItems() {
      const now = Date.now();
      let changed = false;

      this.items.forEach((item) => {
        const timestamp = Number(item.dataset.classTimestamp);
        if (!Number.isFinite(timestamp) || timestamp <= 0) return;

        if (timestamp * 1000 <= now) {
          changed = this.finishItem(item) || changed;
        }
      });

      return changed;
    }

    scheduleNextExpiration() {
      window.clearTimeout(this.expirationTimeout);
      this.expirationTimeout = null;

      const now = Date.now();
      const futureTimestamps = this.items
        .filter((item) => item.dataset.finished !== 'true')
        .map((item) => Number(item.dataset.classTimestamp) * 1000)
        .filter((timestamp) => Number.isFinite(timestamp) && timestamp > now);

      if (!futureTimestamps.length) return;

      const nextExpiration = Math.min(...futureTimestamps);
      const delay = Math.min(Math.max(nextExpiration - now + 25, 0), MAX_TIMEOUT);

      this.expirationTimeout = window.setTimeout(() => {
        if (!this.initialized || !this.isConnected) return;

        const changed = this.refreshExpiredItems();
        this.updateSummary();
        if (changed) {
          this.showWarning('Una clase finalizó y fue retirada de tu selección.');
        }
      }, delay);
    }

    getActiveIndex() {
      return this.tabs.findIndex((tab) => tab.classList.contains('bc-tab--active'));
    }

    updateArrows() {
      const activeIndex = this.getActiveIndex();
      const activePanel = this.panels.find((panel) => panel.classList.contains('bc-panel--active'));
      if (!activePanel) return;

      const previousButton = activePanel.querySelector('[data-bundle-prev]');
      const nextButton = activePanel.querySelector('[data-bundle-next]');
      if (previousButton) previousButton.disabled = activeIndex === 0;
      if (nextButton) nextButton.disabled = activeIndex === this.tabs.length - 1;
    }

    goToTab(index) {
      if (index < 0 || index >= this.tabs.length) return;

      this.tabs.forEach((tab) => {
        tab.classList.remove('bc-tab--active');
        tab.setAttribute('aria-selected', 'false');
      });
      this.panels.forEach((panel) => panel.classList.remove('bc-panel--active'));

      const activeTab = this.tabs[index];
      const activePanel = this.panels.find((panel) => panel.dataset.bundlePanel === activeTab.dataset.tab);
      activeTab.classList.add('bc-tab--active');
      activeTab.setAttribute('aria-selected', 'true');
      activePanel?.classList.add('bc-panel--active');

      if (this.selectionPrompt && !this.selectionPrompt.hidden) {
        activePanel?.querySelector('[data-bundle-grid]')?.append(this.selectionPrompt);
      }

      this.updateArrows();
    }

    showShake(item) {
      item.classList.add('shake');
      const timeout = window.setTimeout(() => {
        item.classList.remove('shake');
        this.shakeTimeouts.delete(timeout);
      }, 500);
      this.shakeTimeouts.add(timeout);
    }

    showWarning(message) {
      if (!this.warningMessage) return;
      this.warningMessage.textContent = message;
      this.warningMessage.hidden = false;
    }

    hideWarning() {
      if (!this.warningMessage) return;
      this.warningMessage.hidden = true;
      this.warningMessage.textContent = '';
    }

    showSelectionPrompt() {
      if (!this.selectionPrompt) return;

      const activePanel = this.panels.find((panel) => panel.classList.contains('bc-panel--active'));
      const activeGrid = activePanel?.querySelector('[data-bundle-grid]');
      if (!activeGrid) return;

      window.clearTimeout(this.selectionPromptTimeout);
      activeGrid.append(this.selectionPrompt);
      this.selectionPrompt.hidden = false;
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      activeGrid.scrollIntoView({ block: 'start', behavior });
      this.selectionPromptTimeout = window.setTimeout(() => {
        this.hideSelectionPrompt();
      }, 5000);
    }

    hideSelectionPrompt() {
      window.clearTimeout(this.selectionPromptTimeout);
      this.selectionPromptTimeout = null;
      if (this.selectionPrompt) this.selectionPrompt.hidden = true;
    }

    dispatchCartState(detail) {
      document.dispatchEvent(new CustomEvent(CART_STATE_EVENT, {
        detail: { ...detail, source: this },
      }));
    }

    handleCartState(event) {
      const detail = event.detail || {};
      if (!this.initialized || detail.source === this) return;

      if (typeof detail.busy === 'boolean') this.cartBusy = detail.busy;
      if (typeof detail.cartVerified === 'boolean') this.cartVerified = detail.cartVerified;
      if (typeof detail.itemsExist === 'boolean') this.itemsExist = detail.itemsExist;

      if (detail.cleared) {
        this.querySelectorAll('.bundle-course__input').forEach((input) => {
          input.checked = false;
        });
        this.showCartClearedAlert();
      }

      this.updateSummary();
      if (this.itemsExist) this.showWarning('Ya tienes productos en el carrito.');
    }

    setCartBusy(busy, broadcast = true) {
      this.cartBusy = busy;
      if (this.initialized && this.isConnected) this.updateSummary();
      if (broadcast) this.dispatchCartState({ busy });
    }

    async checkCart({ broadcast = true } = {}) {
      if (!this.addButton) return false;

      this.cartCheckController?.abort();
      const controller = new AbortController();
      const token = this.lifecycleToken;
      this.cartCheckController = controller;

      try {
        const response = await fetch('/cart.json', { signal: controller.signal });
        if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);

        const cart = await response.json();
        if (!Array.isArray(cart.items)) throw new Error('Cart response is invalid');
        if (
          !this.initialized
          || !this.isConnected
          || token !== this.lifecycleToken
          || controller.signal.aborted
          || this.cartCheckController !== controller
        ) {
          return false;
        }

        this.cartCheckController = null;
        this.itemsExist = cart.items.length > 0;
        this.cartVerified = true;
        this.updateSummary();
        if (this.itemsExist) this.showWarning('Ya tienes productos en el carrito.');
        if (broadcast) {
          this.dispatchCartState({
            cartVerified: true,
            itemsExist: this.itemsExist,
          });
        }
        return !this.itemsExist;
      } catch (error) {
        if (error.name === 'AbortError') return false;
        if (
          !this.initialized
          || !this.isConnected
          || token !== this.lifecycleToken
          || this.cartCheckController !== controller
        ) {
          return false;
        }

        this.cartCheckController = null;
        this.cartVerified = false;
        this.itemsExist = false;
        console.error('Error checking cart:', error);
        this.updateSummary();
        this.showWarning('No pudimos verificar el carrito. Inténtalo nuevamente.');
        return false;
      }
    }

    async clearCart() {
      if (this.cartBusy) return;

      this.setCartBusy(true);
      try {
        const response = await fetch('/cart/clear.js', { method: 'POST' });
        if (!response.ok) throw new Error(`Clear cart failed: ${response.status}`);

        this.itemsExist = false;
        this.cartVerified = true;
        this.querySelectorAll('.bundle-course__input').forEach((input) => {
          input.checked = false;
        });
        this.showCartClearedAlert();
        this.dispatchCartState({
          cartVerified: true,
          itemsExist: false,
          cleared: true,
        });
      } catch (error) {
        console.error('Error clearing cart:', error);
        this.cartBusy = false;
        this.updateSummary();
        this.dispatchCartState({ busy: false });
        this.showWarning('No pudimos vaciar el carrito. Inténtalo nuevamente.');
      } finally {
        if (this.cartBusy) this.setCartBusy(false);
      }
    }

    showCartClearedAlert() {
      if (!this.alertBox) return;

      window.clearTimeout(this.alertTimeout);
      this.alertBox.hidden = false;
      this.alertBox.classList.add('show');
      this.alertTimeout = window.setTimeout(() => {
        this.alertBox.classList.remove('show');
        this.alertBox.hidden = true;
      }, 3000);
    }

    updateSummary() {
      this.refreshExpiredItems();
      this.scheduleNextExpiration();
      if (!this.countElement || !this.discountedPriceElement || !this.addButton) return;

      if (!this.cartVerified || this.itemsExist || this.cartBusy) {
        this.countElement.textContent = '0';
        if (this.regularPriceElement) this.regularPriceElement.textContent = '';
        this.discountedPriceElement.textContent = '$0';
        if (this.savingsMessage) this.savingsMessage.hidden = true;
        this.addButton.disabled = true;
        if (this.clearButton) {
          this.clearButton.hidden = !this.itemsExist;
          this.clearButton.disabled = this.cartBusy;
        }
        return;
      }

      if (this.clearButton) {
        this.clearButton.hidden = true;
        this.clearButton.disabled = false;
      }

      const selectedInputs = [
        ...this.querySelectorAll('.bundle-course__input:checked:not(:disabled)'),
      ];
      const count = selectedInputs.length;

      this.countElement.textContent = String(count);
      if (this.savingsMessage) this.savingsMessage.hidden = true;
      this.hideWarning();

      if (count === 0) {
        if (this.regularPriceElement) this.regularPriceElement.textContent = '';
        this.discountedPriceElement.textContent = '$0';
        this.addButton.disabled = true;
        return;
      }

      const pricePerClass = 67;
      const normalPrice = pricePerClass * count;
      const packagePrice10 = 597;

      if (this.package10Enabled && count === 10) {
        const savings = normalPrice - packagePrice10;
        if (this.regularPriceElement) this.regularPriceElement.textContent = `$${normalPrice}`;
        this.discountedPriceElement.textContent = `$${packagePrice10}`;
        if (this.savingsMessage) {
          this.savingsMessage.textContent = `¡Ahorras $${savings}!`;
          this.savingsMessage.hidden = false;
        }
      } else {
        if (this.regularPriceElement) this.regularPriceElement.textContent = '';
        this.discountedPriceElement.textContent = `$${normalPrice}`;
      }

      this.addButton.disabled = false;
    }

    handleChange(event) {
      const input = event.target.closest('.bundle-course__input');
      if (!input || !this.contains(input)) return;

      if (input.checked) this.hideSelectionPrompt();
      this.refreshExpiredItems();
      if (!this.cartVerified || this.itemsExist || this.cartBusy || input.disabled) {
        this.updateSummary();
        return;
      }
      this.updateSummary();
    }

    handleClick(event) {
      const packageTrigger = event.target.closest('[data-bundle-package-trigger]');
      if (packageTrigger && this.contains(packageTrigger)) {
        this.showSelectionPrompt();
        return;
      }

      const tab = event.target.closest('[data-bundle-tab]');
      if (tab && this.contains(tab)) {
        this.goToTab(this.tabs.indexOf(tab));
        return;
      }

      if (event.target.closest('[data-bundle-prev]')) {
        this.goToTab(this.getActiveIndex() - 1);
        return;
      }

      if (event.target.closest('[data-bundle-next]')) {
        this.goToTab(this.getActiveIndex() + 1);
        return;
      }

      if (event.target.closest('[data-bundle-annual-add]')) {
        this.addAnnualMembership();
        return;
      }

      if (event.target.closest('[data-bundle-clear]')) {
        this.clearCart();
        return;
      }

      if (event.target.closest('[data-bundle-add]')) {
        this.addSelectedItems();
        return;
      }

      const item = event.target.closest('.bundle-course__item');
      if (item && this.contains(item) && this.itemsExist) this.showShake(item);
    }

    async addAnnualMembership() {
      const annualSection = this.querySelector('[data-bundle-annual]');
      const variantId = Number(annualSection?.dataset.annualVariant);
      if (!Number.isFinite(variantId) || variantId <= 0) return;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        if (!response.ok) throw new Error(`Promo product add failed: ${response.status}`);

        window.location.href = '/cart';
      } catch (error) {
        console.error('Error adding promo product:', error);
        this.showWarning('No pudimos agregar el producto al carrito. Inténtalo nuevamente.');
      }
    }

    async addSelectedItems() {
      if (!this.cartVerified || this.itemsExist || this.cartBusy) return;

      this.setCartBusy(true);
      try {
        const cartIsEmpty = await this.checkCart();
        if (!cartIsEmpty) return;

        this.refreshExpiredItems();
        this.updateSummary();
        const now = Date.now();
        const selectedInputs = [
          ...this.querySelectorAll('.bundle-course__input:checked:not(:disabled)'),
        ];
        const items = selectedInputs
          .filter((input) => {
            const timestampValue = input.closest('.bundle-course__item')?.dataset.classTimestamp?.trim();
            if (!timestampValue) return true;

            const timestamp = Number(timestampValue) * 1000;
            return Number.isFinite(timestamp) && timestamp > now;
          })
          .map((input) => Number(input.value))
          .filter((variantId) => Number.isFinite(variantId) && variantId > 0)
          .map((variantId) => ({ id: variantId, quantity: 1 }));

        if (!items.length) return;

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) throw new Error(`Bundle add failed: ${response.status}`);

        this.itemsExist = true;
        this.cartVerified = true;
        this.dispatchCartState({ cartVerified: true, itemsExist: true });
        window.location.href = this.package10Enabled && items.length === 10 ? '/cart?origen=descuento' : '/cart';
      } catch (error) {
        console.error('Error adding bundle to cart:', error);
        this.cartBusy = false;
        this.updateSummary();
        this.dispatchCartState({ busy: false });
        this.showWarning('No pudimos agregar las clases al carrito. Inténtalo nuevamente.');
      } finally {
        if (this.cartBusy) this.setCartBusy(false);
      }
    }
  }

  if (!customElements.get('bundle-course')) {
    customElements.define('bundle-course', BundleCourse);
  }
})();
